import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Mic, 
  MicOff, 
  Send, 
  X, 
  MessageSquare,
  Sparkles,
  Loader2,
  Volume2,
  MapPin,
  Route,
  Search,
  Calendar,
  CheckCircle,
  Users,
  UserPlus,
  Clock,
  Building2,
  Navigation,
  CheckSquare,
  Square
} from 'lucide-react';

export default function VoiceAssistant() {
  const { api } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'voice', 'route', 'new-clients'
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);
  
  // Route planner state
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [routeForm, setRouteForm] = useState({
    category: '',
    city: '',
    district: '',
    zone: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    max_stops: 5
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  
  // Client confirmation state - for selecting which clients to add
  const [pendingClients, setPendingClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null); // 'search', 'web-search', 'filter'
  
  // New clients route planner state
  const [newClientsForm, setNewClientsForm] = useState({
    client_type: '',
    district: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    min_stops: 20,
    visit_duration_minutes: 30,
    start_time: '09:00',
    notes: '',
    search_web: true
  });
  const [newClientsResult, setNewClientsResult] = useState(null);
  const [headquarters, setHeadquarters] = useState(null);
  
  // Portugal districts for dropdown
  const PORTUGAL_DISTRICTS = [
    'Aveiro', 'Beja', 'Braga', 'Bragança', 'Castelo Branco',
    'Coimbra', 'Évora', 'Faro', 'Guarda', 'Leiria',
    'Lisboa', 'Portalegre', 'Porto', 'Santarém', 'Setúbal',
    'Viana do Castelo', 'Vila Real', 'Viseu'
  ];
  
  // Search history
  const [searchHistory, setSearchHistory] = useState([]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory]);

  useEffect(() => {
    if (isOpen && (activeTab === 'route' || activeTab === 'new-clients')) {
      loadCategoriesAndCities();
    }
    if (isOpen && activeTab === 'new-clients') {
      if (!headquarters) loadHeadquarters();
      loadSearchHistory();
    }
  }, [isOpen, activeTab]);

  const loadSearchHistory = async () => {
    try {
      const res = await api().get('/search-history');
      setSearchHistory(res.data || []);
    } catch (error) {
      console.error('Error loading search history', error);
    }
  };

  const applySearchFromHistory = (search) => {
    setNewClientsForm({
      ...newClientsForm,
      client_type: search.client_type,
      district: search.district
    });
    toast.info(`Pesquisa carregada: ${search.client_type} em ${search.district}`);
  };

  const loadHeadquarters = async () => {
    try {
      const res = await api().get('/headquarters');
      setHeadquarters(res.data);
    } catch (error) {
      console.error('Error loading headquarters', error);
    }
  };

  const loadCategoriesAndCities = async () => {
    try {
      const [catRes, cityRes] = await Promise.all([
        api().get('/contacts/categories'),
        api().get('/contacts/cities')
      ]);
      setCategories(catRes.data.categories || []);
      setCities(cityRes.data.cities || []);
      setDistricts(cityRes.data.districts || []);
    } catch (error) {
      console.error('Error loading categories/cities', error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      toast.error('Não foi possível aceder ao microfone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const transcribeResponse = await api().post('/ai/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const transcribedText = transcribeResponse.data.transcript;
      setTranscript(transcribedText);
      
      // Process voice report
      const reportResponse = await api().post('/ai/voice-report', {
        transcript: transcribedText
      });
      
      setChatHistory(prev => [
        ...prev,
        { role: 'user', content: transcribedText, type: 'voice' },
        { role: 'assistant', content: reportResponse.data.summary, type: 'voice-report' }
      ]);
      
      toast.success('Relatório de voz processado!');
    } catch (error) {
      toast.error('Erro ao processar áudio');
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    const userMessage = inputMessage;
    setInputMessage('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsProcessing(true);
    
    try {
      const response = await api().post('/ai/assistant', {
        message: userMessage
      });
      
      setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Erro ao comunicar com o assistente');
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: 'Desculpe, ocorreu um erro. Por favor, tente novamente.' 
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const searchClients = async () => {
    if (!searchQuery.trim()) return;
    
    setIsProcessing(true);
    try {
      const response = await api().post('/ai/search-clients', {
        query: searchQuery
      });
      setSearchResults(response.data);
      
      if (response.data.total_found > 0) {
        toast.success(`Encontrados ${response.data.total_found} clientes`);
      } else {
        toast.info('Nenhum cliente encontrado');
      }
    } catch (error) {
      toast.error('Erro na pesquisa');
    } finally {
      setIsProcessing(false);
    }
  };

  const planRoute = async () => {
    setIsProcessing(true);
    try {
      // First, get the clients that would be added (preview mode)
      const response = await api().post('/ai/plan-route-preview', routeForm);
      
      if (response.data.success && response.data.clients?.length > 0) {
        // Show confirmation modal with clients to select
        setPendingClients(response.data.clients);
        const initialSelection = {};
        response.data.clients.forEach(c => { initialSelection[c.id] = true; });
        setSelectedClients(initialSelection);
        setConfirmationType('filter');
        setShowConfirmation(true);
        toast.info(`${response.data.clients.length} clientes encontrados. Confirme os que deseja adicionar.`);
      } else {
        toast.error(response.data.message || 'Nenhum cliente encontrado com estes critérios');
      }
    } catch (error) {
      // Fallback to old behavior if preview endpoint doesn't exist
      try {
        const response = await api().post('/ai/plan-route', routeForm);
        if (response.data.success) {
          toast.success(response.data.message);
          setChatHistory(prev => [
            ...prev,
            { 
              role: 'assistant', 
              content: `🗺️ **Rota Criada: ${response.data.route.name}**\n\n${response.data.explanation}\n\n✅ ${response.data.route.stops.length} paragens adicionadas`,
              type: 'route-created'
            }
          ]);
          setActiveTab('chat');
        } else {
          toast.error(response.data.message);
        }
      } catch (err) {
        toast.error('Erro ao planear rota');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const planNewClientsRoute = async () => {
    if (!newClientsForm.client_type.trim()) {
      toast.error('Por favor, indique o tipo de cliente');
      return;
    }
    if (!newClientsForm.district) {
      toast.error('Por favor, selecione o distrito');
      return;
    }
    
    setIsProcessing(true);
    setNewClientsResult(null);
    toast.info('A pesquisar empresas na internet...');
    
    try {
      // First, search for businesses without creating the route
      const response = await api().post('/ai/search-businesses-web-preview', newClientsForm);
      
      if (response.data.success && response.data.businesses?.length > 0) {
        // Show confirmation modal with businesses to select
        setPendingClients(response.data.businesses.map(b => ({
          id: b.id || `new-${Math.random().toString(36).substr(2, 9)}`,
          name: b.name,
          address: b.address,
          phone: b.phone,
          category: newClientsForm.client_type,
          lat: b.lat,
          lng: b.lng,
          isNew: true
        })));
        const initialSelection = {};
        response.data.businesses.forEach((b, i) => { 
          initialSelection[b.id || `new-${i}`] = true; 
        });
        setSelectedClients(initialSelection);
        setConfirmationType('web-search');
        setShowConfirmation(true);
        toast.info(`${response.data.businesses.length} empresas encontradas. Confirme as que deseja adicionar à rota.`);
      } else {
        // Fallback to old behavior
        const fallbackResponse = await api().post('/ai/search-businesses-web', newClientsForm);
        if (fallbackResponse.data.success) {
          setNewClientsResult(fallbackResponse.data);
          toast.success(`${fallbackResponse.data.businesses_found} empresas encontradas e rota criada!`);
        } else {
          toast.error(fallbackResponse.data.message);
        }
      }
    } catch (error) {
      // Fallback to old behavior
      try {
        const response = await api().post('/ai/search-businesses-web', newClientsForm);
        if (response.data.success) {
          setNewClientsResult(response.data);
          toast.success(`${response.data.businesses_found} empresas encontradas e rota criada!`);
        } else {
          toast.error(response.data.message);
        }
      } catch (err) {
        toast.error('Erro ao pesquisar e planear rota');
        console.error(err);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Toggle client selection
  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  // Select/Deselect all clients
  const toggleSelectAll = () => {
    const allSelected = Object.values(selectedClients).every(v => v);
    const newSelection = {};
    pendingClients.forEach(c => { newSelection[c.id] = !allSelected; });
    setSelectedClients(newSelection);
  };

  // Confirm and create route with selected clients
  const confirmRouteCreation = async (createRoute = true) => {
    const clientsToAdd = pendingClients.filter(c => selectedClients[c.id]);
    
    if (clientsToAdd.length === 0) {
      toast.error('Selecione pelo menos um cliente');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const response = await api().post('/ai/create-route-confirmed', {
        clients: clientsToAdd,
        date: confirmationType === 'web-search' ? newClientsForm.date : routeForm.date,
        route_type: confirmationType === 'web-search' ? 'diaria' : 'diaria',
        confirmation_type: confirmationType,
        create_route: createRoute  // New flag to control route creation
      });
      
      if (response.data.success) {
        if (createRoute) {
          toast.success(`${clientsToAdd.length} clientes adicionados e rota criada!`);
          setChatHistory(prev => [
            ...prev,
            { 
              role: 'assistant', 
              content: `✅ **${clientsToAdd.length} clientes adicionados à lista**\n\n🗺️ **Rota Criada: ${response.data.route?.name || 'Nova Rota'}**`,
              type: 'route-created'
            }
          ]);
        } else {
          toast.success(`${clientsToAdd.length} clientes adicionados à lista!`);
          setChatHistory(prev => [
            ...prev,
            { 
              role: 'assistant', 
              content: `✅ **${clientsToAdd.length} novos clientes adicionados à sua lista de clientes**\n\nPode criar uma rota com estes clientes a qualquer momento.`,
              type: 'clients-added'
            }
          ]);
        }
        setShowConfirmation(false);
        setPendingClients([]);
        setSelectedClients({});
        setActiveTab('chat');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Erro ao criar rota');
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Cancel confirmation
  const cancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingClients([]);
    setSelectedClients({});
    setConfirmationType(null);
  };

  const createRouteFromSearch = async () => {
    if (!searchResults?.clients?.length) return;
    
    setIsProcessing(true);
    try {
      const stops = searchResults.clients
        .filter(c => c.lat && c.lng)
        .slice(0, 10)
        .map(c => ({
          client_id: c.id,
          client_name: c.name,
          address: c.address,
          lat: c.lat,
          lng: c.lng,
          visited: false
        }));
      
      if (stops.length === 0) {
        toast.error('Nenhum cliente com localização GPS');
        return;
      }
      
      const routeData = {
        name: `Rota ${searchQuery} - ${format(new Date(), 'dd/MM')}`,
        route_type: 'diaria',
        date: format(new Date(), 'yyyy-MM-dd'),
        stops: stops
      };
      
      await api().post('/routes', routeData);
      toast.success(`Rota criada com ${stops.length} paragens!`);
      setSearchResults(null);
      setSearchQuery('');
    } catch (error) {
      toast.error('Erro ao criar rota');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 h-14 w-14 rounded-full bg-indigo-800 hover:bg-indigo-900 shadow-lg z-[9999]"
        data-testid="voice-assistant-btn"
      >
        <Sparkles className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-20 right-6 w-[420px] h-[550px] shadow-2xl z-[9999] flex flex-col" data-testid="voice-assistant-panel">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-[Manrope] flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            Assistente IA
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-1 mt-2 flex-wrap">
          <Button
            variant={activeTab === 'chat' ? 'default' : 'outline'}
            size="sm"
            className={activeTab === 'chat' ? 'bg-indigo-800' : ''}
            onClick={() => setActiveTab('chat')}
            data-testid="chat-tab-btn"
          >
            <MessageSquare className="h-4 w-4 mr-1" />
            Chat
          </Button>
          <Button
            variant={activeTab === 'new-clients' ? 'default' : 'outline'}
            size="sm"
            className={activeTab === 'new-clients' ? 'bg-emerald-600' : ''}
            onClick={() => setActiveTab('new-clients')}
            data-testid="new-clients-tab-btn"
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Novos
          </Button>
          <Button
            variant={activeTab === 'route' ? 'default' : 'outline'}
            size="sm"
            className={activeTab === 'route' ? 'bg-amber-500 text-slate-900' : ''}
            onClick={() => setActiveTab('route')}
            data-testid="route-tab-btn"
          >
            <Route className="h-4 w-4 mr-1" />
            Rota
          </Button>
          <Button
            variant={activeTab === 'voice' ? 'default' : 'outline'}
            size="sm"
            className={activeTab === 'voice' ? 'bg-indigo-800' : ''}
            onClick={() => setActiveTab('voice')}
            data-testid="voice-tab-btn"
          >
            <Mic className="h-4 w-4 mr-1" />
            Voz
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-4 pt-0 overflow-hidden">
        {activeTab === 'new-clients' ? (
          /* New Clients Route Planner Tab */
          <ScrollArea className="flex-1">
            <div className="space-y-4 pr-2">
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-start gap-2">
                  <Search className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Pesquisar Empresas na Internet</p>
                    <p className="text-xs text-emerald-700 mt-1">
                      Indique o tipo de empresa e o distrito. A IA vai pesquisar na internet e criar uma rota otimizada!
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Headquarters Info */}
              {headquarters && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-slate-600" />
                    <div>
                      <span className="font-medium">Ponto de Partida:</span>
                      <span className="ml-1">{headquarters.name}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">{headquarters.address}</p>
                </div>
              )}
              
              {/* Search History */}
              {searchHistory.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    Pesquisas Anteriores
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {searchHistory.slice(0, 5).map((search) => (
                      <Badge
                        key={search.id}
                        variant="outline"
                        className="cursor-pointer hover:bg-slate-100 text-xs"
                        onClick={() => applySearchFromHistory(search)}
                      >
                        {search.client_type} • {search.district}
                        <span className="ml-1 text-muted-foreground">({search.results_count})</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Client Type Input */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  Tipo de Cliente a Visitar *
                </Label>
                <Input
                  placeholder="Ex: lojas de construção civil, restaurantes, supermercados..."
                  value={newClientsForm.client_type}
                  onChange={(e) => setNewClientsForm({...newClientsForm, client_type: e.target.value})}
                  data-testid="new-clients-type-input"
                />
                <p className="text-xs text-muted-foreground">
                  A IA vai pesquisar este tipo de empresas na internet
                </p>
              </div>

              {/* District Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  Distrito *
                </Label>
                <Select 
                  value={newClientsForm.district} 
                  onValueChange={(v) => setNewClientsForm({...newClientsForm, district: v})}
                >
                  <SelectTrigger className="h-9" data-testid="new-clients-district-select">
                    <SelectValue placeholder="Selecione o distrito" />
                  </SelectTrigger>
                  <SelectContent>
                    {PORTUGAL_DISTRICTS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  A pesquisa será feita neste distrito
                </p>
              </div>
              
              {/* Route Settings */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Data
                  </Label>
                  <Input
                    type="date"
                    className="h-9"
                    value={newClientsForm.date}
                    onChange={(e) => setNewClientsForm({...newClientsForm, date: e.target.value})}
                    data-testid="new-clients-date-input"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Hora Início
                  </Label>
                  <Input
                    type="time"
                    className="h-9"
                    value={newClientsForm.start_time}
                    onChange={(e) => setNewClientsForm({...newClientsForm, start_time: e.target.value})}
                    data-testid="new-clients-time-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Mín. Paragens</Label>
                  <Select 
                    value={newClientsForm.min_stops.toString()} 
                    onValueChange={(v) => setNewClientsForm({...newClientsForm, min_stops: parseInt(v)})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 15, 20, 25, 30].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} lojas</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs">Tempo por Visita</Label>
                  <Select 
                    value={newClientsForm.visit_duration_minutes.toString()} 
                    onValueChange={(v) => setNewClientsForm({...newClientsForm, visit_duration_minutes: parseInt(v)})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[15, 20, 30, 45, 60].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Notas (opcional)</Label>
                <Textarea
                  placeholder="Instruções adicionais para a rota..."
                  className="h-16 resize-none"
                  value={newClientsForm.notes}
                  onChange={(e) => setNewClientsForm({...newClientsForm, notes: e.target.value})}
                />
              </div>
              
              {/* Result Stats */}
              {newClientsResult && (
                <div className={`p-3 rounded-lg ${newClientsResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  {newClientsResult.success ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-emerald-700 font-medium">
                        <CheckCircle className="h-4 w-4" />
                        Rota Criada com Sucesso!
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {newClientsResult.stats.total_clients} clientes
                        </div>
                        <div className="flex items-center gap-1">
                          <Navigation className="h-3 w-3" />
                          ~{newClientsResult.stats.total_distance_km} km
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {newClientsResult.stats.total_time}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {newClientsResult.stats.start_time} - {newClientsResult.stats.estimated_end_time}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-red-700 text-sm">
                      <p className="font-medium">{newClientsResult.message}</p>
                      {newClientsResult.stats && (
                        <p className="text-xs mt-1">
                          Encontrados: {newClientsResult.stats.new_clients_found} | Necessário: {newClientsResult.stats.min_required}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={planNewClientsRoute}
                disabled={isProcessing || !newClientsForm.client_type.trim() || !newClientsForm.district}
                data-testid="plan-new-clients-btn"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    A pesquisar na internet...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Pesquisar e Criar Rota
                  </>
                )}
              </Button>
            </div>
          </ScrollArea>
        ) : activeTab === 'route' ? (
          /* Route Planner Tab */
          <ScrollArea className="flex-1">
            <div className="space-y-4 pr-2">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <Sparkles className="h-4 w-4 inline mr-1" />
                  Pesquise clientes por categoria e zona, e crie uma rota automaticamente!
                </p>
              </div>
              
              {/* Quick Search */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Pesquisa Inteligente</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ex: restaurantes em Lisboa, lojas no Porto..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchClients()}
                    data-testid="ai-search-input"
                  />
                  <Button 
                    onClick={searchClients} 
                    disabled={isProcessing || !searchQuery.trim()}
                    className="bg-indigo-800 hover:bg-indigo-900"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Search Results */}
              {searchResults && (
                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {searchResults.total_found} clientes encontrados
                    </span>
                    {searchResults.total_found > 0 && (
                      <Button 
                        size="sm" 
                        className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                        onClick={createRouteFromSearch}
                        disabled={isProcessing}
                      >
                        <Route className="h-4 w-4 mr-1" />
                        Criar Rota
                      </Button>
                    )}
                  </div>
                  
                  {searchResults.explanation && (
                    <p className="text-xs text-muted-foreground">{searchResults.explanation}</p>
                  )}
                  
                  <div className="max-h-[150px] overflow-y-auto space-y-1">
                    {searchResults.clients?.map((client) => (
                      <div key={client.id} className="flex items-center gap-2 text-sm p-2 bg-white rounded border">
                        <Users className="h-4 w-4 text-slate-400" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{client.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {client.category} • {client.city || client.address}
                          </p>
                        </div>
                        {client.lat && client.lng && (
                          <MapPin className="h-4 w-4 text-emerald-500" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="border-t pt-4">
                <Label className="text-sm font-medium mb-3 block">Ou use filtros específicos:</Label>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Categoria</Label>
                    <Select 
                      value={routeForm.category || "all"} 
                      onValueChange={(v) => setRouteForm({...routeForm, category: v === "all" ? "" : v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Cidade</Label>
                    <Select 
                      value={routeForm.city || "all"} 
                      onValueChange={(v) => setRouteForm({...routeForm, city: v === "all" ? "" : v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {cities.map(city => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      className="h-9"
                      value={routeForm.date}
                      onChange={(e) => setRouteForm({...routeForm, date: e.target.value})}
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Máx. Paragens</Label>
                    <Select 
                      value={routeForm.max_stops.toString()} 
                      onValueChange={(v) => setRouteForm({...routeForm, max_stops: parseInt(v)})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[3, 5, 7, 10, 15].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n} paragens</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-1 mt-3">
                  <Label className="text-xs">Zona/Região (opcional)</Label>
                  <Input
                    placeholder="Ex: zona norte, centro histórico..."
                    className="h-9"
                    value={routeForm.zone}
                    onChange={(e) => setRouteForm({...routeForm, zone: e.target.value})}
                  />
                </div>
                
                <Button 
                  className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900"
                  onClick={planRoute}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Planear Rota com IA
                </Button>
              </div>
            </div>
          </ScrollArea>
        ) : (
          <>
            {/* Chat History */}
            <ScrollArea className="flex-1 pr-4" ref={scrollRef}>
              <div className="space-y-4">
                {chatHistory.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">
                      {activeTab === 'chat' 
                        ? 'Pergunte-me sobre vendas, estratégias ou análise de dados!'
                        : 'Grave um relatório de visita e eu processo para si!'
                      }
                    </p>
                  </div>
                )}
                
                {chatHistory.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`
                      max-w-[85%] rounded-lg p-3 text-sm
                      ${msg.role === 'user' 
                        ? 'bg-indigo-800 text-white' 
                        : msg.type === 'voice-report'
                          ? 'bg-slate-50 border-l-4 border-indigo-500'
                          : msg.type === 'route-created'
                            ? 'bg-amber-50 border-l-4 border-amber-500'
                            : 'bg-slate-100'
                      }
                    `}>
                      {msg.type === 'voice' && (
                        <div className="flex items-center gap-1 text-xs mb-1 opacity-70">
                          <Volume2 className="h-3 w-3" />
                          Transcrição
                        </div>
                      )}
                      {msg.type === 'voice-report' && (
                        <div className="flex items-center gap-1 text-xs mb-2 text-indigo-600 font-medium">
                          <Sparkles className="h-3 w-3" />
                          Relatório Processado
                        </div>
                      )}
                      {msg.type === 'route-created' && (
                        <div className="flex items-center gap-1 text-xs mb-2 text-amber-600 font-medium">
                          <CheckCircle className="h-3 w-3" />
                          Rota Criada
                        </div>
                      )}
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                
                {isProcessing && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-lg p-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            
            {/* Input Area */}
            <div className="mt-4 space-y-3">
              {activeTab === 'voice' ? (
                <div className="flex flex-col items-center gap-3">
                  <Button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing}
                    className={`
                      h-16 w-16 rounded-full transition-all
                      ${isRecording 
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                        : 'bg-indigo-800 hover:bg-indigo-900'
                      }
                    `}
                    data-testid="record-btn"
                  >
                    {isRecording ? (
                      <MicOff className="h-6 w-6" />
                    ) : (
                      <Mic className="h-6 w-6" />
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    {isRecording 
                      ? 'A gravar... Clique para parar'
                      : isProcessing
                        ? 'A processar...'
                        : 'Clique para gravar relatório'
                    }
                  </p>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Escreva a sua mensagem..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="min-h-[60px] resize-none"
                    disabled={isProcessing}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isProcessing}
                    className="h-auto bg-indigo-800 hover:bg-indigo-900"
                    data-testid="send-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Client Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="w-[95vw] max-w-[500px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-500" />
              Confirmar Clientes para a Rota
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col gap-4 py-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-sm text-muted-foreground">
                {Object.values(selectedClients).filter(v => v).length} de {pendingClients.length} selecionados
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={toggleSelectAll}
                className="text-xs"
              >
                {Object.values(selectedClients).every(v => v) ? (
                  <>
                    <Square className="h-3 w-3 mr-1" />
                    Desmarcar Todos
                  </>
                ) : (
                  <>
                    <CheckSquare className="h-3 w-3 mr-1" />
                    Selecionar Todos
                  </>
                )}
              </Button>
            </div>
            
            <ScrollArea className="flex-1 max-h-[350px] border rounded-lg">
              <div className="p-2 space-y-1">
                {pendingClients.map((client) => (
                  <div 
                    key={client.id}
                    className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors
                      ${selectedClients[client.id] ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50 border border-transparent'}
                    `}
                    onClick={() => toggleClientSelection(client.id)}
                  >
                    <Checkbox 
                      checked={selectedClients[client.id] || false}
                      onCheckedChange={() => toggleClientSelection(client.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{client.name}</p>
                        {client.isNew && (
                          <Badge className="text-[10px] bg-blue-100 text-blue-700">Novo</Badge>
                        )}
                      </div>
                      {client.address && (
                        <p className="text-xs text-muted-foreground truncate">{client.address}</p>
                      )}
                      {client.phone && (
                        <p className="text-xs text-slate-500">{client.phone}</p>
                      )}
                      {client.category && (
                        <Badge variant="outline" className="text-[10px] mt-1">{client.category}</Badge>
                      )}
                    </div>
                    {client.lat && client.lng && (
                      <MapPin className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
            
            <p className="text-xs text-muted-foreground px-1">
              Clique para selecionar ou desmarcar. <strong>Apenas os clientes selecionados serão adicionados à sua lista de clientes.</strong>
            </p>
          </div>
          
          <DialogFooter className="flex-col gap-2 pt-2">
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              {/* Add to clients only */}
              {confirmationType === 'web-search' && (
                <Button 
                  variant="outline"
                  onClick={() => confirmRouteCreation(false)}
                  disabled={isProcessing || Object.values(selectedClients).filter(v => v).length === 0}
                  className="w-full sm:flex-1"
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Users className="h-4 w-4 mr-2" />
                  )}
                  Só Adicionar aos Clientes
                </Button>
              )}
              
              {/* Add to clients AND create route */}
              <Button 
                onClick={() => confirmRouteCreation(true)}
                disabled={isProcessing || Object.values(selectedClients).filter(v => v).length === 0}
                className="w-full sm:flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    A processar...
                  </>
                ) : (
                  <>
                    <Route className="h-4 w-4 mr-2" />
                    Adicionar + Criar Rota ({Object.values(selectedClients).filter(v => v).length})
                  </>
                )}
              </Button>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={cancelConfirmation}
              className="w-full text-muted-foreground"
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
